import type { ReactNode } from 'react';

/** A `{ value, label, count }` row from a `/…/filter-options` endpoint. */
export interface Facet {
  value: string | number;
  label?: string | number | null;
  count?: number | null;
}

export interface FacetOption {
  value: string | number;
  label: string;
  /** Kept off `label` so the picker's search + selected chip never see it. */
  count?: number | null;
}

/**
 * Facet rows → Select options, with the count kept OUT of the label.
 *
 * Baking the count into the label (`"Pune (35)"`) makes it part of what the picker searches and of
 * what the selected chip shows — typing "35" would match Pune, and the chip would read "Pune (35)".
 * Here the label stays the bare value and the count rides alongside, rendered right-aligned by
 * `renderFacetOption` (pass it as the Select's `optionRender`).
 *
 * `label` remaps the display text (e.g. a state code → its name); rows whose value is blank are
 * dropped — an unlabelled option is unusable.
 */
export function facetOptions(
  rows: Facet[] | undefined | null,
  label?: (row: Facet) => string,
): FacetOption[] {
  return (rows ?? [])
    .filter((r) => String(r.value ?? '').trim() !== '')
    .map((r) => ({
      value: r.value,
      label: (label ? label(r) : String(r.label ?? r.value)).trim(),
      count: r.count,
    }));
}

/**
 * How many rows an option leaves, as its own small badge at the row's right edge.
 *
 * "Shingve · 1" printed the count as part of the name, one dot away from it. A badge in its own
 * column reads as a different fact at a glance, and a 0 is visibly a 0 rather than a stray digit.
 */
export function FacetCount({ n }: { n: number }) {
  return (
    <span className="inline-flex min-w-6 shrink-0 items-center justify-center rounded-pill border border-line bg-surface-2 px-1.5 type-caption font-normal tabular-nums text-muted">
      {n}
    </span>
  );
}

/**
 * Select `optionRender` for facet options: the label on the left, its count as a badge on the right,
 * so a long list reads as two columns instead of a ragged "name · n" run. The count lives on the
 * option's `count`, never in its label, so the closed control shows the bare name.
 */
export function renderFacetOption(o: any): ReactNode {
  const count = o.count ?? o.data?.count;
  return (
    <span className="flex w-full items-center justify-between gap-4">
      <span className="min-w-0">{o.label ?? o.data?.label}</span>
      {count != null && <FacetCount n={count} />}
    </span>
  );
}
